/**
 * Grid System Node Configs
 *
 * Each config defines a system type that appears as its own node.
 * Systems are pure GridState → GridState transforms.
 */

import type { Endpoint } from '@nodes/Endpoint';
import type { GridTransform } from '../gridTransform';
import { flow, drift, convert, spread, grow, decay, neighbourReduce, threshold } from '../gridTransform';
import type { ReduceMode, OffsetPreset } from '../gridTransform';

export interface GridTransformConfig {
  type: string;
  shortName: string;
  label: string;
  info: string;
  /** String params for layer names (user types the layer name). */
  layerParams: Record<string, { label: string; info?: string }>;
  /** Tweakable number/option params. */
  params: Record<string, Partial<Endpoint>>;
  /** Create the grid system from resolved param values. */
  createGridTransform(params: Record<string, any>): GridTransform;
}

export const allTransformConfigs: GridTransformConfig[] = [
  {
    type: 'Flow',
    shortName: 'flow',
    label: 'Flow',
    info: 'Move a property down the gradient of another layer.',
    layerParams: {
      property: { label: 'Property', info: 'Layer to move (e.g. water)' },
      gradient: { label: 'Gradient', info: 'Height layer (e.g. terrain)' },
    },
    params: {
      rate: { value: 0.25, min: 0, max: 1, step: 0.01 },
    },
    createGridTransform(p) {
      return flow(p.property, p.gradient, p.rate);
    },
  },
  {
    type: 'Drift',
    shortName: 'drift',
    label: 'Drift',
    info: 'Shift a property in a fixed direction with wrapping.',
    layerParams: {
      property: { label: 'Property', info: 'Layer to drift' },
    },
    params: {
      dx: { value: 1, min: -10, max: 10, step: 1 },
      dy: { value: 0, min: -10, max: 10, step: 1 },
      strength: { value: 0.3, min: 0, max: 1, step: 0.01 },
    },
    createGridTransform(p) {
      return drift(p.property, p.dx, p.dy, p.strength);
    },
  },
  {
    type: 'Spread',
    shortName: 'spread',
    label: 'Spread',
    info: 'Diffuse a property evenly to neighbours.',
    layerParams: {
      property: { label: 'Property', info: 'Layer to spread' },
    },
    params: {
      rate: { value: 0.1, min: 0, max: 1, step: 0.01 },
    },
    createGridTransform(p) {
      return spread(p.property, p.rate);
    },
  },
  {
    type: 'Convert',
    shortName: 'conv',
    label: 'Convert',
    info: 'Transfer between layers, optionally influenced by a third.',
    layerParams: {
      from: { label: 'From', info: 'Source layer' },
      to: { label: 'To', info: 'Destination layer' },
      influence: { label: 'Influence', info: 'Influence layer (optional)' },
    },
    params: {
      rate: { value: 0.05, min: 0, max: 1, step: 0.01 },
      invert: { value: 0, options: [0, 1], info: 'Invert influence (1 = faster at LOW values)' },
    },
    createGridTransform(p) {
      const inf = p.influence
        ? { layer: p.influence, inverse: !!p.invert }
        : undefined;
      return convert(p.from, p.to, p.rate, inf);
    },
  },
  {
    type: 'Grow',
    shortName: 'grow',
    label: 'Grow',
    info: 'Logistic growth of a property.',
    layerParams: {
      property: { label: 'Property', info: 'Layer to grow' },
      influence: { label: 'Influence', info: 'Influence layer (optional)' },
    },
    params: {
      rate: { value: 0.1, min: 0, max: 1, step: 0.01 },
      capacity: { value: 1, min: 0, max: 10, step: 0.1 },
      invert: { value: 0, options: [0, 1], info: 'Invert influence' },
    },
    createGridTransform(p) {
      const inf = p.influence
        ? { layer: p.influence, inverse: !!p.invert }
        : undefined;
      return grow(p.property, p.rate, p.capacity, inf);
    },
  },
  {
    type: 'Decay',
    shortName: 'decay',
    label: 'Decay',
    info: 'Exponential decay of a property.',
    layerParams: {
      property: { label: 'Property', info: 'Layer to decay' },
      influence: { label: 'Influence', info: 'Influence layer (optional)' },
    },
    params: {
      rate: { value: 0.05, min: 0, max: 1, step: 0.01 },
      invert: { value: 0, options: [0, 1], info: 'Invert influence' },
    },
    createGridTransform(p) {
      const inf = p.influence
        ? { layer: p.influence, inverse: !!p.invert }
        : undefined;
      return decay(p.property, p.rate, inf);
    },
  },
  {
    type: 'NeighbourReduce',
    shortName: 'nreduce',
    label: 'Neighbour Reduce',
    info: 'Compute a neighbour aggregate (sum/count/avg) and write to a target layer.',
    layerParams: {
      source: { label: 'Source', info: 'Layer to read neighbours from' },
      target: { label: 'Target', info: 'Layer to write result to' },
    },
    params: {
      mode: { value: 'count', options: ['sum', 'count', 'avg'] as unknown as number[], info: 'Reduction mode' },
      offsets: { value: 'moore', options: ['moore', 'vonneumann'] as unknown as number[], info: 'Neighbourhood' },
    },
    createGridTransform(p) {
      return neighbourReduce(p.source, p.target, p.mode as ReduceMode, p.offsets as OffsetPreset);
    },
  },
  {
    type: 'Threshold',
    shortName: 'thresh',
    label: 'Threshold',
    info: 'Conditional step function: birth/survive rules based on a count layer.',
    layerParams: {
      source: { label: 'Source', info: 'Layer to test alive/dead (> 0.5 = alive)' },
      count: { label: 'Count', info: 'Neighbour count layer (from NeighbourReduce)' },
      target: { label: 'Target', info: 'Layer to write result to (can be same as source)' },
    },
    params: {
      birthMin: { value: 3, min: 0, max: 8, step: 1, info: 'Min neighbours for birth' },
      birthMax: { value: 3, min: 0, max: 8, step: 1, info: 'Max neighbours for birth' },
      surviveMin: { value: 2, min: 0, max: 8, step: 1, info: 'Min neighbours for survival' },
      surviveMax: { value: 3, min: 0, max: 8, step: 1, info: 'Max neighbours for survival' },
    },
    createGridTransform(p) {
      return threshold(p.source, p.count, p.target, p.birthMin, p.birthMax, p.surviveMin, p.surviveMax);
    },
  },
];
